import { RouteComponentProps } from "../../../models/RouteComponent";
import { Typography } from "@mui/joy";
import React, { useState, useEffect } from "react";
import useApiService from "../../../hooks/useApiService";
import { DtpInterface } from "../../../models/DSTI/site-dsti.interface";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { formatDateTime } from "../../../utils/DateUtils";
import { StatusBadge } from "../../../components/StatusBadge/StatusBadge";
import { DSTIRecord } from "../../../models/DSTI/site-dsti-list.interface";
import { Dropdown } from "primereact/dropdown";
import { Eye, PencilEdit, Trash } from "../../../assets/icons";
import { useNavigate } from "react-router-dom";
import { Paginator } from "primereact/paginator";
import { capitaliseFirstLetter } from "../../../utils/generalUtils";

type StatusDtpInterface = DtpInterface & {
  status: string;
};

type PaginationType = {
  first: number;
  rows: number;
  page: number;
  totalPages: number;
};

function DailyTaskPlan(props: RouteComponentProps) {
  const [paginationParams, setPaginationParams] = useState({
    first: 0,
    rows: 10,
    page: 0,
  });
  const [dailyTaskPlans, setDailyTaskPlans] = useState<StatusDtpInterface[]>();
  const [totalRecords, setTotalRecords] = useState<number>();
  const navigate = useNavigate();
  const { loading: loadingDtpList, fetchData: fetchDtpList } = useApiService<{
    content: DtpInterface[];
    totalRecords: number;
  }>({
    method: "GET",
    url: "dtp",
    apiName: "core",
    includeTotal: true,
    // fetchOnMount: false,
    params: {
      rows: paginationParams.rows,
      page: paginationParams.page,
    },
    onSuccess: data => {
      const grouped: { [date: string]: StatusDtpInterface[] } = {};
      data.content.forEach(obj => {
        const date = obj.created_at.slice(0, 10);
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(obj as StatusDtpInterface);
      });

      Object.values(grouped).forEach(group => {
        const latest = group.reduce((a, b) =>
          new Date(a.created_at) > new Date(b.created_at) ? a : b
        );
        latest.status = "latest";
      });

      const result: StatusDtpInterface[] = Object.values(grouped).flat();

      setDailyTaskPlans(result);
      setTotalRecords(data.totalRecords);
    },
  });

  const handlePagination = async (e: PaginationType) => {
    setPaginationParams({
      first: e.first,
      rows: e.rows,
      page: e.page,
    });

    const res = await fetchDtpList({
      fetchParams: {
        rows: e.rows,
        page: e.page,
      },
    });

    if (res) {
      // Process and set only the current page's data
      const grouped: { [date: string]: StatusDtpInterface[] } = {};
      res.content.forEach(obj => {
        const date = obj.created_at.slice(0, 10);
        if (!grouped[date]) grouped[date] = [];
        grouped[date].push(obj as StatusDtpInterface);
      });

      Object.values(grouped).forEach(group => {
        const latest = group.reduce((a, b) =>
          new Date(a.created_at) > new Date(b.created_at) ? a : b
        );
        latest.status = "latest";
      });
      const result: StatusDtpInterface[] = Object.values(grouped).flat();
      setDailyTaskPlans(result);
      setTotalRecords(res.totalRecords);
    }
  };

  const displayDate = (rowData: StatusDtpInterface) => {
    return <span>{formatDateTime(rowData.created_at)}</span>;
  };

  const displaySignatureStatus = (rowData: StatusDtpInterface) => {
    return (
      <StatusBadge variant={rowData.signature ? "success" : "warning"}>
        {rowData.signature ? "Signed" : "Not Signed"}
      </StatusBadge>
    );
  };

  const displayStatus = (rowData: StatusDtpInterface) => {
    if (rowData.status) {
      return (
        <StatusBadge variant={rowData.status === "latest" ? "info" : "warning"}>
          {capitaliseFirstLetter(rowData.status)}
        </StatusBadge>
      );
    }
    return <span>–</span>;
  };

  const optionTemplate = (option: { label: string; value: string }) => {
    switch (option.value) {
      case "view":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <Eye />
            <div>{option.label}</div>
          </div>
        );
      case "edit":
      case "start":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <PencilEdit />
            <div>{option.label}</div>
          </div>
        );

      case "delete":
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <Trash />
            <div>{option.label}</div>
          </div>
        );
      default:
        return (
          <div className="flex align-items-center gap-2" style={{ minWidth: "4rem" }}>
            <div>{option.label}</div>
          </div>
        );
    }
  };

  const displaySettings = (rowData: StatusDtpInterface) => {
    let options: { label: string; value: string; id: string | number; name: string }[] = [
      { label: "View DTP", value: "view", id: rowData.id, name: "dtp" },
    ];
    if (rowData.status === "latest") {
      options = options.concat({
        label: "Start DTP",
        value: "start",
        id: rowData.id,
        name: "start-dtp",
      });
    }
    return (
      //   e.stopPropagation is used
      //   to prevent the row click event from being triggered when clicking on the settings icon
      <div onClick={e => e.stopPropagation()}>
        <Dropdown
          options={options}
          dropdownIcon={"pi pi-cog"}
          placeholder=" "
          className="p-dropdown-icon-only"
          style={{
            border: "none",
            background: "transparent",
            boxShadow: "none",
            minWidth: "auto",
          }}
          showClear={false}
          onChange={val => {
            if (val.value === "view") {
              navigate(`/daily-task-plan/${rowData.id}`);
            }
            if (val.value === "start") {
              navigate({ pathname: `/daily-task-plan/${rowData.id}/start` });
            }
          }}
          itemTemplate={optionTemplate}
          panelStyle={{ minWidth: "350px" }}
        />
      </div>
    );
  };

  return (
    <>
      <Typography level="h2" component="h1">
        Daily Task Plan (DTP)
      </Typography>
      <DataTable
        value={dailyTaskPlans}
        className={`policies-procedures-table`}
        tableStyle={{ minWidth: "50rem" }}
        loading={loadingDtpList}
        onRowClick={rowData => navigate(`/daily-task-plan/${rowData.data.id}`)}
      >
        <Column field={"created_at"} header={"Date"} body={displayDate} />
        <Column field={"created_by_full_name"} header={"Created by"} />
        <Column field={"signature"} header={"Signature Status"} body={displaySignatureStatus} />
        <Column field={"status"} header={"Status"} body={displayStatus} />
        <Column field={"_"} header={"Settings"} body={displaySettings} />
      </DataTable>
      <Paginator
        first={paginationParams.first}
        rows={paginationParams.rows}
        totalRecords={totalRecords}
        rowsPerPageOptions={[5, 10, 20, 50]}
        onPageChange={async page => {
          // @ts-ignore
          await handlePagination(page as PaginationType);
        }}
      />
    </>
  );
}

export default DailyTaskPlan;
